/**
 * Magazyn: drugi panel przy prawej krawędzi, obok panelu pola.
 *
 * **Dwie zakładki, jeden budynek.** „Przekaż" pokazuje sakwę i przenosi do
 * budynku, „Wyciągnij" pokazuje magazyn budynku i przenosi do sakwy. To jest
 * jeden ruch w dwie strony, więc jedno miejsce — a nie dwa okienka z własnymi
 * regułami zamykania.
 *
 * **Obok panelu pola, nie zamiast niego.** Panel pola mówi, czym budynek jest
 * i co z nim zrobić; ten mówi, co w nim leży. Gdy panelu pola nie ma — stoi się
 * obok budynku, a nie na nim — magazyn zajmuje jego miejsce przy krawędzi.
 *
 * **Pola z liczbą przeżywają odświeżenie.** Panel dostaje nowe liczby dwa razy
 * na sekundę, a wpisywana ilość nie może wracać do domyślnej w pół słowa, więc
 * wiersze są przebudowywane tylko wtedy, gdy zmienia się **zestaw** surowców;
 * inaczej zmienia się wyłącznie to, ile czego jest.
 */

export type StoreTab = 'give' | 'take';

export interface StoreItem {
  material: string;
  amount: number;
  /** Ikona i nazwa, gotowe do pokazania. */
  label: string;
}

interface Row {
  count: HTMLElement;
  amount: HTMLInputElement;
  held: number;
}

export class StorePanel {
  private readonly root = document.createElement('div');
  private readonly title = document.createElement('div');
  private readonly tabs = document.createElement('div');
  private readonly note = document.createElement('div');
  private readonly list = document.createElement('div');
  private readonly buttons: Record<StoreTab, HTMLButtonElement>;

  private tab: StoreTab = 'give';
  private rows = new Map<string, Row>();
  /** Jaki zestaw surowców stoi dziś w wierszach — żeby nie przebudowywać bez potrzeby. */
  private painted = '';

  constructor(
    container: HTMLElement,
    private readonly handlers: {
      move: (tab: StoreTab, material: string, amount: number) => void;
      close: () => void;
      /** Zakładka zmieniona ręką — właściciel dopyta o nowe liczby. */
      switched: () => void;
    }
  ) {
    this.root.className = 'store';
    this.root.hidden = true;
    this.root.addEventListener('contextmenu', (event) => event.preventDefault());

    const head = document.createElement('div');
    head.className = 'store-head';
    this.title.className = 'store-title';
    head.append(this.title);
    // Wyjście na dole, jak w każdym panelu bocznym.
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'side-close';
    close.textContent = 'zamknij';
    close.addEventListener('click', () => this.handlers.close());

    this.tabs.className = 'store-tabs';
    const tab = (which: StoreTab, label: string): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'store-tab';
      button.textContent = label;
      button.addEventListener('click', () => {
        if (this.tab === which) return;
        this.select(which);
        this.handlers.switched();
      });
      this.tabs.appendChild(button);
      return button;
    };
    this.buttons = { give: tab('give', 'Przekaż'), take: tab('take', 'Wyciągnij') };

    this.note.className = 'store-note';
    this.list.className = 'store-list';

    this.root.append(head, this.tabs, this.note, this.list, close);
    container.appendChild(this.root);
  }

  get open(): boolean {
    return !this.root.hidden;
  }

  get current(): StoreTab {
    return this.tab;
  }

  /** Otwórz na tej zakładce. */
  select(tab: StoreTab): void {
    this.tab = tab;
    this.buttons.give.classList.toggle('active', tab === 'give');
    this.buttons.take.classList.toggle('active', tab === 'take');
    this.painted = '';
  }

  /**
   * Pokaż, co jest do przeniesienia w wybranej zakładce.
   *
   * `beside` mówi, czy magazyn stoi na lewo od panelu pola (otwarty z niego),
   * czy przy samej krawędzi (otwarty z menu pod prawym przyciskiem).
   */
  show(title: string, items: StoreItem[], beside: boolean): void {
    this.title.textContent = title;
    this.root.classList.toggle('store-beside', beside);

    const present = items.filter((item) => item.amount > 0);
    const stamp = `${this.tab}|${present.map((item) => item.material).join(',')}`;
    if (stamp !== this.painted) {
      this.painted = stamp;
      this.rebuild(present);
    }

    for (const item of present) {
      const row = this.rows.get(item.material);
      if (!row) continue;
      row.held = item.amount;
      row.count.textContent = `${item.label} ${item.amount}`;
      row.amount.max = String(item.amount);
    }

    this.note.textContent =
      present.length > 0
        ? ''
        : this.tab === 'give'
          ? 'nie masz nic przy sobie'
          : 'magazyn budynku jest pusty';
    this.note.hidden = present.length > 0;
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
    this.painted = '';
  }

  dispose(): void {
    this.root.remove();
  }

  private rebuild(items: StoreItem[]): void {
    this.rows.clear();
    const verb = this.tab === 'give' ? 'przekaż' : 'wyciągnij';

    this.list.replaceChildren(
      ...items.map((item) => {
        const line = document.createElement('div');
        line.className = 'store-row';

        const count = document.createElement('span');
        count.className = 'store-what';

        const amount = document.createElement('input');
        amount.type = 'number';
        amount.className = 'store-amount';
        amount.min = '1';
        amount.value = String(item.amount);

        const row: Row = { count, amount, held: item.amount };
        const send = (many: number) => {
          const clamped = Math.min(row.held, Math.max(1, Math.floor(many || 0)));
          if (row.held > 0) this.handlers.move(this.tab, item.material, clamped);
        };

        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'store-go';
        go.textContent = verb;
        go.addEventListener('click', () => send(Number(amount.value)));

        const all = document.createElement('button');
        all.type = 'button';
        all.className = 'store-all';
        all.textContent = 'wszystko';
        all.addEventListener('click', () => send(row.held));

        this.rows.set(item.material, row);
        line.append(count, amount, go, all);
        return line;
      })
    );
  }
}
